import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { AppAutocomplete } from "components/common/inputs";
import { AppTab } from "components/common/tabs";

export const ASSIGNEE_SOURCE_TYPES = {
  EXECUTION_GROUP: "EXECUTION_GROUP",
  DEPARTMENT: "DEPARTMENT",
  DEPARTMENT_UNIT: "DEPARTMENT_UNIT",
};

const SOURCE_TABS = [
  { value: ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP, label: "Nhóm thực hiện giao khoán", sourceType: ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP },
  { value: "PHONG", label: "Phòng", sourceType: ASSIGNEE_SOURCE_TYPES.DEPARTMENT },
  { value: "BAN", label: "Ban", sourceType: ASSIGNEE_SOURCE_TYPES.DEPARTMENT },
  { value: "DOI", label: "Đội", sourceType: ASSIGNEE_SOURCE_TYPES.DEPARTMENT },
  { value: "PHAN_XUONG", label: "Phân xưởng", sourceType: ASSIGNEE_SOURCE_TYPES.DEPARTMENT },
];

const buildValue = (sourceType, sourceId) => `${sourceType}:${sourceId}`;

const getField = (item, camelName, pascalName) => item?.[camelName] ?? item?.[pascalName];

const optionPrimaryText = (option) => option?.leaderName || "Chưa có người đại diện";

const optionSecondaryText = (option) => {
  if (option?.sourceType === ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP) {
    return option?.label || "";
  }

  if (option?.sourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT) {
    return [option?.label, option?.enterpriseName].filter(Boolean).join(" - ");
  }

  if (option?.sourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT_UNIT) {
    return [option?.label, option?.secondary, option?.enterpriseName].filter(Boolean).join(" - ");
  }

  return option?.label || "";
};

const filterOptions = createFilterOptions({
  stringify: (option) => [
    option?.label,
    option?.code,
    option?.secondary,
    option?.enterpriseName,
    option?.leaderName,
  ].filter(Boolean).join(" "),
});

export default function SubcontractAssigneeAutocomplete({
  value,
  onChange,
  activeSourceType,
  onActiveSourceTypeChange,
  executionGroups = [],
  departments = [],
  departmentUnits = [],
  disabled = false,
  required = false,
}) {
  const [activeDepartmentType, setActiveDepartmentType] = useState("PHONG");
  const selectedDepartment = value?.sourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT
    ? departments.find((item) => String(getField(item, "departmentId", "DepartmentId")) === String(value.sourceId))
    : null;
  const departmentType = getField(selectedDepartment, "departmentType", "DepartmentType") || activeDepartmentType;
  const activeTab = activeSourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT ? departmentType : activeSourceType;
  const tabs = activeSourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT_UNIT
    ? [...SOURCE_TABS, { value: ASSIGNEE_SOURCE_TYPES.DEPARTMENT_UNIT, label: "Đơn vị trực thuộc đã chọn", sourceType: ASSIGNEE_SOURCE_TYPES.DEPARTMENT_UNIT }]
    : SOURCE_TABS;

  const optionsBySource = useMemo(() => ({
    [ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP]: executionGroups.map((item) => ({
      value: buildValue(ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP, getField(item, "subcontractExecutionGroupId", "SubcontractExecutionGroupId")),
      sourceType: ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP,
      sourceId: getField(item, "subcontractExecutionGroupId", "SubcontractExecutionGroupId"),
      label: getField(item, "subcontractExecutionGroupName", "SubcontractExecutionGroupName"),
      code: getField(item, "subcontractExecutionGroupCode", "SubcontractExecutionGroupCode"),
      leaderName: getField(item, "leaderFullName", "LeaderFullName"),
    })),
    [ASSIGNEE_SOURCE_TYPES.DEPARTMENT]: departments.map((item) => ({
      value: buildValue(ASSIGNEE_SOURCE_TYPES.DEPARTMENT, getField(item, "departmentId", "DepartmentId")),
      sourceType: ASSIGNEE_SOURCE_TYPES.DEPARTMENT,
      sourceId: getField(item, "departmentId", "DepartmentId"),
      departmentType: getField(item, "departmentType", "DepartmentType"),
      label: getField(item, "departmentName", "DepartmentName"),
      code: getField(item, "departmentCode", "DepartmentCode"),
      enterpriseName: getField(item, "enterpriseName", "EnterpriseName"),
      leaderName: getField(item, "leaderFullName", "LeaderFullName"),
    })),
    [ASSIGNEE_SOURCE_TYPES.DEPARTMENT_UNIT]: departmentUnits.map((item) => ({
      value: buildValue(ASSIGNEE_SOURCE_TYPES.DEPARTMENT_UNIT, getField(item, "departmentUnitId", "DepartmentUnitId")),
      sourceType: ASSIGNEE_SOURCE_TYPES.DEPARTMENT_UNIT,
      sourceId: getField(item, "departmentUnitId", "DepartmentUnitId"),
      label: getField(item, "departmentUnitName", "DepartmentUnitName"),
      code: getField(item, "departmentUnitCode", "DepartmentUnitCode"),
      secondary: getField(item, "departmentName", "DepartmentName"),
      enterpriseName: getField(item, "enterpriseName", "EnterpriseName"),
      leaderName: getField(item, "leaderFullName", "LeaderFullName"),
    })),
  }), [departmentUnits, departments, executionGroups]);

  const sourceOptions = optionsBySource[activeSourceType] || [];
  const options = activeSourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT
    ? sourceOptions.filter((option) => option.departmentType === departmentType)
    : activeSourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT_UNIT
      ? sourceOptions.filter((option) => String(option.sourceId) === String(value?.sourceId))
      : sourceOptions;
  const selectedValue = value?.sourceType === activeSourceType && value?.sourceId
    ? buildValue(value.sourceType, value.sourceId)
    : "";

  const handleTabChange = (_, nextTab) => {
    const category = tabs.find((tab) => tab.value === nextTab);
    if (!category) return;
    if (category.sourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT) setActiveDepartmentType(category.value);
    onActiveSourceTypeChange?.(category.sourceType);
  };

  return (
    <AppAutocomplete
      required={required}
      label="Đơn vị thực hiện"
      value={selectedValue}
      disabled={disabled}
      options={options}
      noOptionsText="Không tìm thấy đơn vị thực hiện"
      filterOptions={filterOptions}
      paperHeader={(
        <Box
          onMouseDown={(event) => event.preventDefault()}
          sx={{ borderBottom: "1px solid #e2e8f0", px: 0.75, pt: 0.5 }}
        >
          <AppTab
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Loại đơn vị thực hiện"
            tabs={tabs.map((tab) => ({ ...tab, disabled }))}
            allowScrollButtonsMobile
            sx={{
              minHeight: 30,
              "& .MuiTab-root": {
                minHeight: 30,
                px: 0.75,
                py: 0.5,
                textTransform: "none",
                fontSize: 12,
                fontWeight: 650,
              },
              "& .MuiTabs-indicator": {
                height: 2,
              },
            }}
          />
        </Box>
      )}
      paperSx={{
        "& .MuiAutocomplete-noOptions": {
          fontSize: 12,
          py: 1,
        },
      }}
      onChange={(_, option) => {
        onChange?.(option ? {
          sourceType: option.sourceType,
          sourceId: option.sourceId,
          name: option.label,
          code: option.code || "",
          leaderName: option.leaderName || "",
        } : null);
      }}
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {optionPrimaryText(option)}
            </Typography>
            {optionSecondaryText(option) && (
              <Typography variant="caption" color="text.secondary">
                {optionSecondaryText(option)}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    />
  );
}
